/**
 * DueLessonsCard — Practice tab entry point for spaced-repetition review.
 *
 * Shows how many lessons are due (from the shared lessonStore, loaded on
 * focus) and opens the Due for review screen. Fetch failures are silent:
 * the card falls back to a neutral CTA — the Practice screen must never
 * grow an error surface for a secondary card (DUE-003).
 *
 * The card stays visible with nothing due; it is a standing entry point,
 * not a notification (DUE-002).
 *
 * @see docs/specs/mobile/lessons.mobile.md § LESSON-006
 */

import React, { useCallback } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import Ionicons from '../ui/Ionicons';
import { FS_XS, FS_SM, FS_MD } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { useLessonStore } from '../../store/lessonStore';
import {
  selectDueTotal,
  selectDueLoaded,
  selectLoadDueLessons,
} from '../../store/lessonSelectors';

const FS_EMOJI_MD = 20;

export const DueLessonsCard = React.memo(function DueLessonsCard() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const dueTotal = useLessonStore(selectDueTotal);
  const dueLoaded = useLessonStore(selectDueLoaded);
  const loadDueLessons = useLessonStore(selectLoadDueLessons);

  // Load on focus — completing a lesson and coming back should move the
  // count (DUE-009). The store keeps the last good data on failure, so the
  // card degrades to the neutral CTA only when nothing has ever loaded.
  useFocusEffect(
    useCallback(() => {
      void loadDueLessons();
    }, [loadDueLessons]),
  );

  const handlePress = useCallback(() => {
    navigation.navigate('DueLessons');
  }, [navigation]);

  const subtitle = !dueLoaded
    ? t('lessons.due.card.cta')
    : dueTotal === 0
      ? t('lessons.due.card.caughtUp')
      : t('lessons.due.card.count', { count: dueTotal });

  return (
    <TouchableOpacity
      testID="practice-due-lessons-card"
      accessibilityRole="button"
      accessibilityLabel={`${t('lessons.due.card.title')}: ${subtitle}`}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <Text
          allowFontScaling={false}
          accessible={false}
          importantForAccessibility="no"
          style={{ fontSize: FS_EMOJI_MD * fontScale, marginRight: 12 }}
        >
          🔁
        </Text>
        <View style={styles.body}>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
          >
            {t('lessons.due.card.title')}
          </Text>
          <Text
            allowFontScaling={false}
            numberOfLines={2}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
          >
            {subtitle}
          </Text>
        </View>
        <Ionicons
          name="chevron-forward"
          size={Math.round(FS_SM * fontScale) + 4}
          color={theme.textSecondary}
        />
      </View>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 16,
    borderWidth: 1,
  },
  body: {
    flex: 1,
    marginRight: 8,
  },
});
